import * as React from 'react'

import EmptyState from '../empty_state'
import { SkillPerformanceInterface, } from '../interfaces'
import { accountImg, formatDateToShortFormat, TABLE_WIDTH_PROPS, } from '../shared'
import { EXTRA_SMALL_ICON_BASE_SRC, DataTable, Tooltip, Spinner, DataTableChip, } from '../../../../../Shared/index'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'

const helpIcon = <img alt="Question mark inside of circle" src={`${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`} />

interface ByStudentPropsInterface {
  skillPerformances: SkillPerformanceInterface[];
  selectedSkill: { value: number, label: string }
}

const FULL_PROFICIENCY = 'fullProficiency'
const PARTIAL_PROFICIENCY = 'partialProficiency'
const NO_PROFICIENCY = 'noProficiency'
const NO_DATA = 'noData'

const ByStudent = ({ skillPerformances, selectedSkill, additionalRequestRunning, }: ByStudentPropsInterface) => {
  const relevantSkillPerformances = skillPerformances?.find(skillPerformance => skillPerformance.skill_id === selectedSkill?.value)

  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 299, minWidth: 180 },
      { width: 299, minWidth: 180 },
      { width: 299, minWidth: 180 },
      { width: 299, minWidth: 180 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !!relevantSkillPerformances,
    dependencies: [Object.values(columnWidths).join('|')], // ensure resync when columnWidths change
  });

  const headers = [
    {
      name: (
        <span className="header-with-tooltip">
          Full Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student answered all questions for this skill correctly on at least one Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'fullProficiency',
      width: columnWidths['299w-180min'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow full-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'Full Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          Partial Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student answered some questions for this skill correctly on at least one Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'partialProficiency',
      width: columnWidths['299w-180min'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow partial-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'Partial Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          No Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student did not answer any questions for this skill correctly on any Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'noProficiency',
      width: columnWidths['299w-180min'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow no-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'No Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          No Data
          <Tooltip
            skipPositioning={true}
            tooltipText="The student has not completed the selected diagnostic in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'noData',
      width: columnWidths['299w-180min'],
      headerClassName: 'no-data-header eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow no-data',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'No Data'
    },
  ]

  if (!skillPerformances || additionalRequestRunning) {
    return (
      <div className="diagnostic-insights-content performance-insights">
        <Spinner />
      </div>
    )
  }

  if (!skillPerformances.length || !relevantSkillPerformances) {
    return (
      <EmptyState
        bodyText="There is no diagnostic history available for the filters selected. However, there may be history for previous versions of the selected diagnostic. Try selecting another version from the diagnostic dropdown or modifying the timeframe. Alternatively, consider assigning a diagnostic."
        headerText="No performance insights are available"
      />
    )
  }

  const renderCellTooltip = (studentPerformance, column) => {
    if (!studentPerformance) { return }

    const { completed_at, } = studentPerformance

    let tooltipText = 'This student has not completed the selected diagnostic in the selected timeframe.'

    switch(column) {
      case FULL_PROFICIENCY:
        tooltipText = `This student last demonstrated full proficiency in ${selectedSkill.label} on ${formatDateToShortFormat(completed_at)}`
        break;
      case PARTIAL_PROFICIENCY:
        tooltipText = `This student last demonstrated partial proficiency in ${selectedSkill.label} on ${formatDateToShortFormat(completed_at)}`
        break;
      case NO_PROFICIENCY:
        tooltipText = `This student has not demonstrated proficiency in ${selectedSkill.label} in the selected timeframe.`
        break;
    }

    return (
      <Tooltip
        skipPositioning={true}
        tooltipText={tooltipText}
        tooltipTriggerText={<DataTableChip color="grey" icon={accountImg} label={studentPerformance.user_name} />}
      />
    )
  }

  const fullProficiencyPerformances = relevantSkillPerformances.full_proficiency || []
  const partialProficiencyPerformances = relevantSkillPerformances.partial_proficiency || []
  const noProficiencyPerformances = relevantSkillPerformances.no_proficiency || []
  const noDataPerformances = relevantSkillPerformances.no_data || []

  const longestStudentPerformanceCount = Math.max(
    fullProficiencyPerformances.length,
    partialProficiencyPerformances.length,
    noProficiencyPerformances.length,
    noDataPerformances.length,
  )

  const rows = Array.from({ length: longestStudentPerformanceCount }, (_, index) => {
    return {
      id: index,
      fullProficiency: renderCellTooltip(fullProficiencyPerformances[index], FULL_PROFICIENCY),
      partialProficiency: renderCellTooltip(partialProficiencyPerformances[index], PARTIAL_PROFICIENCY),
      noProficiency: renderCellTooltip(noProficiencyPerformances[index], NO_PROFICIENCY),
      noData: renderCellTooltip(noDataPerformances[index], NO_DATA)
    }
  })

  return (
    <section className="diagnostic-insights-content performance-insights" ref={containerRef}>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}
export default ByStudent
